import { log } from 'log';
import { prop } from 'ramda';
import { batch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useComponentConfig } from 'js/hooks/component';
import { useRemoveQueryParams } from 'js/hooks/navigate';
import React, { useEffect, useRef, useState } from 'react';
import { withComponent } from 'js/components/hocs/withComponent';
import { useGetGamesConfiguration, useGetGamesFeed, useLiveGames } from 'js/hooks/games';

import * as Types from './types';
import * as hooks from './hooks';
import * as helpers from './helpers';
import * as actions from './store/actions';
import { GamesTabs } from './components/GamesTabs';
import { GamesFilters } from './components/GamesFilters';
import { GamesContent } from './components/GamesContent';
import { GamesContentEmpty } from './components/GamesContentEmpty';
import { withDynamicReducer, useDynamicReducerSelector, useDynamicReducerDispatch } from './store';

import style from './GamesGrid.scss';

export const GamesGridBare: React.FC<Types.IGamesGridProps> = ({ isViewMoreMode }) => {
    const isMounted = useIsMounted();
    const getGamesFeed = useGetGamesFeed();
    const dispatch = useDynamicReducerDispatch();
    const parseGamesFeed = hooks.useParseGamesFeed();
    const removeQueryParams = useRemoveQueryParams();
    const [isError, setIsError] = useState<boolean>(false);
    const getGamesConfiguration = useGetGamesConfiguration();
    const feedName = useDynamicReducerSelector(prop('feedName'));
    const prevFeedName = useRef<string | undefined>(feedName);
    const [liveLaunchAliases, setLiveLaunchAliases] = useState<string[]>([]);
    const { gamesGridTabs, defaultGameFeed } = useComponentConfig<Types.IGamesGridConfig>();
    const {
        param1: feedNameURLParam,
        param2: categoryNameURLParam
    } = useParams<{ param1: string; param2: string }>();

    useEffect(() => {
        if (!isViewMoreMode) {
            return;
        }

        if (!feedNameURLParam || !categoryNameURLParam) {
            setIsError(true);
            dispatch(actions.toggleIsLoading(false));

            log('error', '[Games Grid] feedNameURLParam and categoryNameURLParam are missing in URL location path.');

            return;
        }

        dispatch(actions.updateGamesFeedName(feedNameURLParam));
    // we don't need feedNameURLParam and categoryNameURLParam in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultGameFeed, dispatch, isViewMoreMode]);

    useEffect(() => {
        if (!isViewMoreMode) {
            const { gamesGridFeedName, gamesGridActiveTab } = helpers.getGamesFeedNameAndTab(gamesGridTabs, defaultGameFeed);

            if (!gamesGridFeedName) {
                setIsError(true);
                dispatch(actions.toggleIsLoading(false));

                log('error', '[Games Grid] gamesGridFeedName is empty.');

                return;
            }

            batch(() => {
                dispatch(actions.setActiveTab(gamesGridActiveTab?.tabName || ''));
                dispatch(actions.updateGamesFeedName(gamesGridFeedName));
            });
        }
    }, [defaultGameFeed, dispatch, gamesGridTabs, isViewMoreMode]);

    useEffect(() => {
        removeQueryParams(['gamesGridTab']);
    }, [removeQueryParams]);

    useEffect(() => {
        if (!feedName || feedName === prevFeedName.current) {
            return;
        }
        // this is important to prevent multiple requests on dependency change
        prevFeedName.current = feedName;

        setIsError(false);
        dispatch(actions.toggleIsLoading(true));

        (async () => {
            try {
                const { games } = await getGamesConfiguration();
                const gamesFeedResponse = await getGamesFeed(feedName);
                const gamesFeed = await parseGamesFeed({ allGames: games, isViewMoreMode, gamesFeed: gamesFeedResponse });

                if (isMounted()) {
                    setLiveLaunchAliases(Array.from(gamesFeed.liveGamesAliases));

                    batch(() => {
                        dispatch(actions.updateGamesFeed(gamesFeed));
                        dispatch(actions.toggleIsLoading(false));
                    });
                }
            } catch (error) {
                if (isMounted()) {
                    setIsError(true);
                    dispatch(actions.toggleIsLoading(false));
                }

                log('error', '[Games Grid] general issue with games feed.', error);
            }
        })();
    }, [
        dispatch, feedName, getGamesFeed, getGamesConfiguration,
        isMounted, parseGamesFeed, isViewMoreMode
    ]);

    useLiveGames({ liveLaunchAliases });

    hooks.useRecentlyPlayedSubscription();
    hooks.useFavoritesSubscription();

    return (
        <div className={`${style.container} cms-games-grid ${isViewMoreMode ? 'cms-games-grid-view-more' : ''}`}>
            {!isViewMoreMode && <GamesTabs />}
            {!isError ? (
                <>
                    <GamesFilters />
                    <GamesContent />
                </>
            ) : (
                <GamesContentEmpty />
            )}
        </div>
    );
};

export const GamesGridViewMoreBare: React.FC = () => <GamesGridBare isViewMoreMode />;
export const GamesGrid = withComponent()(withDynamicReducer()(GamesGridBare));
export const GamesGridViewMore = withComponent()(withDynamicReducer()(GamesGridViewMoreBare));
